import React, { useContext, useEffect, useMemo, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { MenuList, SUPERADMIN_REPORT_KEYS } from "./Menu";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";

/* ================================================================== */
/* Icons — drawn inline, so they never go missing (no icon font).      */
/* ================================================================== */
const ICONS = {
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </>
  ),
  chart: <path d="M4 20h16M7 20v-6M12 20V9M17 20V5" />,
  home: <path d="M3.5 11 12 4l8.5 7M5.5 9.5V20h13V9.5M10 20v-5.5h4V20" />,
  layers: <path d="m12 3.5 8.5 4.75L12 13 3.5 8.25 12 3.5ZM3.5 12.5 12 17.25l8.5-4.75M3.5 16.5 12 21.25l8.5-4.75" />,
  users: (
    <>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 5.2a3 3 0 0 1 0 5.6M18 14.5c1.8.8 3 2.9 3 5.5" />
    </>
  ),
  income: <path d="M12 3.5V14M7.5 9.5 12 14l4.5-4.5M4 15.5V19a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-3.5" />,
  expense: <path d="M12 14V3.5M7.5 8 12 3.5 16.5 8M4 15.5V19a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-3.5" />,
  receipt: <path d="M6 3.5h12v17l-3-2-3 2-3-2-3 2v-17ZM9 8.5h6M9 12h6M9 15.5h3.5" />,
  checkSquare: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="m8.5 12 2.5 2.5 5-5" />
    </>
  ),
  receiptCheck: <path d="M6 3.5h12v17l-3-2-3 2-3-2-3 2v-17ZM9.25 11.25l2 2 3.5-3.5" />,
  report: (
    <>
      <path d="M14 3.5H6.5a1.5 1.5 0 0 0-1.5 1.5v14a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5V8.5l-5-5Z" />
      <path d="M14 3.5v5h5M9 17v-3M12 17v-5M15 17v-2" />
    </>
  ),
  shield: <path d="M12 3.5 19.5 6.5v5.5c0 4.2-3.1 7.7-7.5 8.5-4.4-.8-7.5-4.3-7.5-8.5V6.5L12 3.5ZM9 12l2 2 4-4" />,
  bank: <path d="M3.5 9 12 4.5 20.5 9M4.5 9.5h15M6.5 10v7M10 10v7M14 10v7M17.5 10v7M3.5 20h17" />,
  userCog: (
    <>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 20c0-3.6 2.9-6.5 6.5-6.5 1.2 0 2.3.3 3.3.9" />
      <circle cx="17.5" cy="17.5" r="2" />
      <path d="M17.5 13.5v1.5M17.5 20v1.5M13.5 17.5H15M20 17.5h1.5" />
    </>
  ),
  link: <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />,
  key: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="m11 12 8.5-8.5M16.5 6.5l2.5 2.5M14 9l2 2" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="7.5" ry="2.75" />
      <path d="M4.5 6v12c0 1.5 3.4 2.75 7.5 2.75s7.5-1.25 7.5-2.75V6M4.5 12c0 1.5 3.4 2.75 7.5 2.75s7.5-1.25 7.5-2.75" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h9M17 7h3M4 12h2M10 12h10M4 17h11M19 17h1" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="17" cy="17" r="2" />
    </>
  ),
  search: <path d="M11 18.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15ZM20.5 20.5l-4.2-4.2" />,
  chevron: <path d="m9 6 6 6-6 6" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  dot: <circle cx="12" cy="12" r="3" />,
};

const Icon = ({ name, className = "ps-icon" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {ICONS[name] || ICONS.dot}
  </svg>
);

/* ================================================================== */
/* Styles — kept in this file, no separate CSS needed.                 */
/* Class names start with "ps-" so older sidebar rules don't apply.    */
/* ================================================================== */
const SIDEBAR_CSS = `
.deznav.ps-sidebar {
  --ps-bg: #0f1a30;
  --ps-label: #7f93b2;
  --ps-text: #dfe7f3;
  --ps-sub: #b8c5d9;
  --ps-icon: #9fb0c9;
  --ps-hover: rgba(255, 255, 255, 0.06);
  --ps-active-bg: rgba(74, 222, 128, 0.14);
  --ps-accent: #4ade80;
  --ps-line: rgba(255, 255, 255, 0.12);
  background-color: var(--ps-bg) !important;
  padding-top: 0 !important;
  font-family: inherit;
}

/* Search */
.deznav.ps-sidebar .ps-search {
  position: sticky; top: 0; z-index: 5;
  padding: 14px 14px 8px;
  background: var(--ps-bg);
}
.deznav.ps-sidebar .ps-search-box { position: relative; }
.deznav.ps-sidebar .ps-search-box .ps-search-icon {
  position: absolute; left: 12px; top: 50%;
  width: 16px; height: 16px; margin-top: -8px;
  color: #64748b; pointer-events: none;
}
.deznav.ps-sidebar .ps-search input {
  display: block; width: 100%; height: 40px;
  padding: 8px 34px 8px 36px;
  font-size: 13.5px; color: #1e293b;
  background: #ffffff; border: 1px solid #d5dde9; border-radius: 8px;
  box-shadow: none;
}
.deznav.ps-sidebar .ps-search input::-webkit-search-cancel-button { display: none; }
.deznav.ps-sidebar .ps-search input::placeholder { color: #7a8699; }
.deznav.ps-sidebar .ps-search input:focus {
  outline: none; border-color: #8fb4ee;
  box-shadow: 0 0 0 3px rgba(143, 180, 238, 0.3);
}
.deznav.ps-sidebar .ps-search-clear {
  position: absolute; right: 8px; top: 50%; margin-top: -12px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; padding: 0;
  color: #475569; background: transparent; border: 0; border-radius: 50%;
  cursor: pointer;
}
.deznav.ps-sidebar .ps-search-clear:hover { background: #e2e8f0; }
.deznav.ps-sidebar .ps-search-clear svg { width: 14px; height: 14px; }

/* List */
.deznav .metismenu.ps-nav { padding: 0 10px 28px !important; }
.deznav .metismenu.ps-nav li { position: relative; }

/* Section label: plain and quiet */
.deznav .metismenu.ps-nav > li.ps-section {
  display: block;
  padding: 18px 12px 6px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ps-label) !important;
  line-height: 1.2;
}
.deznav .metismenu.ps-nav > li.ps-section:first-child { padding-top: 6px !important; }

/* Row: icon + text (+ arrow for groups) */
.deznav .metismenu.ps-nav > li > .ps-row {
  display: flex !important;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 40px;
  margin: 1px 0;
  padding: 9px 12px !important;
  font: inherit;
  font-size: 14px !important;
  font-weight: 500;
  line-height: 1.3;
  text-align: left;
  text-decoration: none;
  color: var(--ps-text) !important;
  background: transparent;
  border: 0;
  border-radius: 8px;
  box-shadow: none;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}
.deznav .metismenu.ps-nav > li > .ps-row::before,
.deznav .metismenu.ps-nav > li > .ps-row::after { content: none !important; display: none !important; }
.deznav .metismenu.ps-nav > li > .ps-row:hover {
  background: var(--ps-hover);
  color: #ffffff !important;
}
.deznav .metismenu.ps-nav > li > .ps-row:focus-visible {
  outline: 2px solid #8fb4ee; outline-offset: -2px;
}

.deznav .metismenu.ps-nav .ps-icon {
  flex: 0 0 20px;
  width: 20px !important;
  height: 20px !important;
  margin: 0 !important;
  color: var(--ps-icon);
  transition: color .15s ease;
}
.deznav .metismenu.ps-nav > li > .ps-row:hover .ps-icon { color: #ffffff; }

.deznav .metismenu.ps-nav .ps-text {
  flex: 1 1 auto; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* Current page */
.deznav .metismenu.ps-nav > li.ps-active > .ps-row {
  background: var(--ps-active-bg);
  color: #ffffff !important;
  font-weight: 600;
}
.deznav .metismenu.ps-nav > li.ps-active > .ps-row::before {
  content: "" !important; display: block !important;
  position: absolute; left: 0; top: 8px; bottom: 8px;
  width: 3px; border-radius: 3px; background: var(--ps-accent);
}
.deznav .metismenu.ps-nav > li.ps-active > .ps-row .ps-icon,
.deznav .metismenu.ps-nav > li.ps-has-active > .ps-row .ps-icon { color: var(--ps-accent); }

/* Group arrow: right when closed, down when open */
.deznav .metismenu.ps-nav .ps-arrow {
  flex: 0 0 16px;
  width: 16px !important; height: 16px !important;
  margin: 0 !important;
  color: var(--ps-icon);
  transition: transform .2s ease;
}
.deznav .metismenu.ps-nav > li.ps-open > .ps-row .ps-arrow { transform: rotate(90deg); }
.deznav .metismenu.ps-nav > li.ps-open > .ps-row { color: #ffffff !important; }

/* Links inside a group: simple indented list with a guide line */
.deznav .metismenu.ps-nav ul.ps-sub {
  position: relative;
  margin: 2px 0 6px !important;
  padding: 0 !important;
  background: transparent !important;
  list-style: none !important;
}
.deznav .metismenu.ps-nav ul.ps-sub::before { content: none !important; }
.deznav .metismenu.ps-nav ul.ps-sub::after {
  content: "" !important; display: block !important;
  position: absolute; left: 22px; top: 2px; bottom: 2px;
  width: 1px; background: var(--ps-line) !important;
}
.deznav .metismenu.ps-nav ul.ps-sub > li > a,
.deznav .metismenu.ps-nav ul.ps-sub > li > a:hover {
  display: block !important;
  margin: 0 0 0 23px;
  padding: 7px 10px 7px 20px !important;
  font-size: 13px !important;
  line-height: 1.35;
  color: var(--ps-sub) !important;
  text-decoration: none;
  border-radius: 0 6px 6px 0;
  transition: background-color .15s ease, color .15s ease;
}
.deznav .metismenu.ps-nav ul.ps-sub > li > a::before,
.deznav .metismenu.ps-nav ul.ps-sub > li > a::after { content: none !important; display: none !important; }
.deznav .metismenu.ps-nav ul.ps-sub > li > a:hover {
  color: #ffffff !important;
  background: var(--ps-hover);
}
.deznav .metismenu.ps-nav ul.ps-sub > li.ps-active > a {
  color: #ffffff !important;
  font-weight: 600;
  background: var(--ps-active-bg);
  box-shadow: inset 2px 0 0 var(--ps-accent);
}

/* Search results */
.deznav .ps-nav .ps-mark { padding: 0 1px; color: #0f1a30; background: #fde68a; border-radius: 2px; }
.deznav .ps-nav .ps-empty { padding: 18px 12px; font-size: 13px; color: var(--ps-label); }

/* Collapsed / mini sidebar: icons only */
[data-sidebar-style="full"][data-layout="vertical"] .menu-toggle .deznav .ps-search,
[data-sidebar-style="mini"] .deznav .ps-search,
[data-sidebar-style="full"][data-layout="vertical"] .menu-toggle .deznav .ps-nav > li.ps-section,
[data-sidebar-style="mini"] .deznav .ps-nav > li.ps-section,
[data-sidebar-style="full"][data-layout="vertical"] .menu-toggle .deznav .ps-nav .ps-text,
[data-sidebar-style="full"][data-layout="vertical"] .menu-toggle .deznav .ps-nav .ps-arrow,
[data-sidebar-style="full"][data-layout="vertical"] .menu-toggle .deznav .ps-nav .ps-sub,
[data-sidebar-style="mini"] .deznav .ps-nav .ps-text,
[data-sidebar-style="mini"] .deznav .ps-nav .ps-arrow,
[data-sidebar-style="mini"] .deznav .ps-nav .ps-sub { display: none !important; }
[data-sidebar-style="full"][data-layout="vertical"] .menu-toggle .deznav .metismenu.ps-nav > li > .ps-row,
[data-sidebar-style="mini"] .deznav .metismenu.ps-nav > li > .ps-row { justify-content: center; padding: 10px 0 !important; }
[data-sidebar-style="full"][data-layout="vertical"] .menu-toggle .deznav .metismenu.ps-nav,
[data-sidebar-style="mini"] .deznav .metismenu.ps-nav { padding: 8px 6px !important; }

/* Wider sidebar on desktop (expanded) so full names fit */
@media only screen and (min-width: 64rem) {
  [data-sidebar-style="full"][data-layout="vertical"] #main-wrapper:not(.menu-toggle) .deznav,
  [data-sidebar-style="full"][data-layout="vertical"] #main-wrapper:not(.menu-toggle) .nav-header { width: 18.5rem; }
  [data-sidebar-style="full"][data-layout="vertical"] #main-wrapper:not(.menu-toggle) .content-body { margin-left: 18.5rem; }
  [data-sidebar-style="full"][data-layout="vertical"] #main-wrapper:not(.menu-toggle) .header,
  [data-sidebar-style="full"][data-layout="vertical"] #main-wrapper:not(.menu-toggle) .footer { padding-left: 18.5rem; }
}

@media (prefers-reduced-motion: reduce) {
  .deznav .ps-nav .ps-arrow, .deznav .ps-nav .ps-row, .deznav .ps-nav .ps-sub a { transition: none; }
}
`;

/* ================================================================== */
/* Menu logic                                                          */
/* ================================================================== */
const normKey = (key) =>
  String(key || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

const superadminReportKeys = new Set(SUPERADMIN_REPORT_KEYS.map(normKey));

const isSection = (item) => item?.classsChange === "menu-title";
const isGroup = (item) => Array.isArray(item?.content) && item.content.length > 0;

// Groups have no permission of their own; their visible links decide
// whether they show. Each page keeps its own moduleKey check.
function filterMenu(items, ctx) {
  const { isSuperadmin, permissionsMap, loaded } = ctx;
  return (items || []).flatMap((item) => {
    if (!item) return [];
    if (isSection(item)) return [item];
    if ((item.superadminOnly || superadminReportKeys.has(normKey(item.moduleKey))) && !isSuperadmin) {
      return [];
    }
    if (Array.isArray(item.content)) {
      const content = filterMenu(item.content, ctx).filter((c) => !isSection(c));
      return content.length ? [{ ...item, content }] : [];
    }
    if (isSuperadmin) return item.to ? [item] : [];
    if (!item.to || !item.moduleKey || !loaded) return [];
    return permissionsMap?.[normKey(item.moduleKey)]?.can_view ? [item] : [];
  });
}

function searchMenu(items, term) {
  if (!term) return items;
  return (items || []).flatMap((item) => {
    if (isSection(item)) return [item];
    if (String(item.title || "").toLowerCase().includes(term)) return [item];
    if (!Array.isArray(item.content)) return [];
    const content = searchMenu(item.content, term);
    return content.length ? [{ ...item, content }] : [];
  });
}

// Drop section labels that have nothing under them.
function pruneEmptySections(items) {
  const result = [];
  let pending = null;
  items.forEach((item) => {
    if (isSection(item)) {
      pending = item;
      return;
    }
    if (pending) result.push(pending);
    pending = null;
    result.push(item);
  });
  return result;
}

const cleanPath = (value) => String(value || "").replace(/\/+$/, "") || "/";
const pathMatches = (path, to) => Boolean(to) && cleanPath(path) === cleanPath(to);
const subtreeMatches = (item, path) =>
  pathMatches(path, item.to) || (item.content || []).some((c) => subtreeMatches(c, path));

const groupId = (item) => `group:${item.moduleKey || item.title}`;
const domId = (id) => `ps-sub-${id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

/* Highlights the matching part of a name while searching. */
const Highlight = ({ text, term }) => {
  if (!term) return text;
  const index = text.toLowerCase().indexOf(term);
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className="ps-mark">{text.slice(index, index + term.length)}</mark>
      {text.slice(index + term.length)}
    </>
  );
};

/* ================================================================== */
/* Rows                                                                */
/* ================================================================== */
const MenuLink = ({ item, path, term, onNavigate }) => {
  const active = pathMatches(path, item.to);
  return (
    <li className={active ? "ps-active" : ""}>
      <Link to={item.to} className="ps-row" onClick={onNavigate} title={item.title} aria-current={active ? "page" : undefined}>
        <Icon name={item.icon} />
        <span className="nav-text ps-text"><Highlight text={item.title} term={term} /></span>
      </Link>
    </li>
  );
};

const MenuGroup = ({ item, path, openGroup, setOpenGroup, term, onNavigate }) => {
  const id = groupId(item);
  const isOpen = Boolean(term) || openGroup === id;
  const hasActive = subtreeMatches(item, path);

  return (
    <li className={`${isOpen ? "ps-open" : ""} ${hasActive ? "ps-has-active" : ""}`}>
      <button
        type="button"
        className="ps-row"
        title={item.title}
        aria-expanded={isOpen}
        aria-controls={domId(id)}
        onClick={() => setOpenGroup(isOpen ? null : id)}
      >
        <Icon name={item.icon} />
        <span className="nav-text ps-text"><Highlight text={item.title} term={term} /></span>
        <Icon name="chevron" className="ps-arrow" />
      </button>
      <Collapse in={isOpen}>
        <div id={domId(id)}>
          <ul className="ps-sub">
            {item.content.map((child, index) => {
              const active = pathMatches(path, child.to);
              return (
                <li key={`${child.moduleKey || child.title}:${index}`} className={active ? "ps-active" : ""}>
                  <Link to={child.to} onClick={onNavigate} title={child.title} aria-current={active ? "page" : undefined}>
                    <Highlight text={child.title} term={term} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </Collapse>
    </li>
  );
};

/* ================================================================== */
/* Sidebar                                                             */
/* ================================================================== */
const SideBar = () => {
  const {
    iconHover, sidebarposition, headerposition, sidebarLayout, ChangeIconSidebar,
  } = useContext(ThemeContext);
  const { pathname } = useLocation();
  const [search, setSearch] = useState("");
  // Only one group is open at a time, so the menu stays short and simple.
  const [openGroup, setOpenGroup] = useState(() => {
    const current = MenuList.find((item) => isGroup(item) && subtreeMatches(item, pathname));
    return current ? groupId(current) : null;
  });
  const [hideOnScroll, setHideOnScroll] = useState(true);

  const role = String(useSelector((state) => state.auth?.user?.role || "")).trim().toLowerCase();
  const isSuperadmin = role === "superadmin";
  const permissionState = useSelector((state) => state.permissions || {});
  const permissionsMap = permissionState.map || {};
  const loaded = Boolean(permissionState.loaded);

  useScrollPosition(({ prevPos, currPos }) => {
    const showing = currPos.y > prevPos.y;
    if (showing !== hideOnScroll) setHideOnScroll(showing);
  }, [hideOnScroll]);

  const allowedMenu = useMemo(
    () => filterMenu(MenuList, { isSuperadmin, permissionsMap, loaded }),
    [isSuperadmin, permissionsMap, loaded],
  );
  const term = search.trim().toLowerCase();
  const shownMenu = useMemo(
    () => pruneEmptySections(searchMenu(allowedMenu, term)),
    [allowedMenu, term],
  );

  // Open the group that holds the current page.
  useEffect(() => {
    const current = allowedMenu.find((item) => isGroup(item) && subtreeMatches(item, pathname));
    if (current) setOpenGroup(groupId(current));
  }, [pathname, allowedMenu]);

  const clearSearch = () => setSearch("");

  const fixed =
    sidebarposition.value === "fixed" &&
    sidebarLayout.value === "horizontal" &&
    headerposition.value === "static" &&
    !hideOnScroll;

  const hasResults = shownMenu.some((item) => !isSection(item));

  return (
    <div
      onMouseEnter={() => ChangeIconSidebar(true)}
      onMouseLeave={() => ChangeIconSidebar(false)}
      className={`deznav border-right ps-sidebar ${iconHover} ${fixed ? "fixed" : ""}`}
    >
      <style dangerouslySetInnerHTML={{ __html: SIDEBAR_CSS }} />
      <div className="deznav-scroll">
        <div className="ps-search">
          <div className="ps-search-box">
            <Icon name="search" className="ps-search-icon" />
            <input
              type="search"
              placeholder="Search menu..."
              aria-label="Search menu"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Escape") clearSearch(); }}
            />
            {search && (
              <button type="button" className="ps-search-clear" onClick={clearSearch} aria-label="Clear search">
                <Icon name="close" className="" />
              </button>
            )}
          </div>
        </div>

        <ul className="metismenu ps-nav" id="menu">
          {shownMenu.map((item, index) => {
            const key = `${item.moduleKey || item.title}:${index}`;
            if (isSection(item)) {
              return <li key={key} className="ps-section">{item.title}</li>;
            }
            if (isGroup(item)) {
              return (
                <MenuGroup
                  key={key}
                  item={item}
                  path={pathname}
                  openGroup={openGroup}
                  setOpenGroup={setOpenGroup}
                  term={term}
                  onNavigate={clearSearch}
                />
              );
            }
            return <MenuLink key={key} item={item} path={pathname} term={term} onNavigate={clearSearch} />;
          })}
          {term && !hasResults && (
            <li className="ps-empty">No menu matches “{search.trim()}”</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default SideBar;
